import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Container,
  Button,
  Avatar,
} from '@mui/material';
import { Link, NavLink, NavLinkProps, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, getAuth } from 'firebase/auth';
import { useDispatch, useSelector } from 'react-redux';
import { authen } from '../../firebase/firebase';
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import Logoapp from '../../../assets/img/logoapp.png';
import usericon from '../../../assets/img/user-img.png';
import ConfirmModal from '../ConfirmModal';
import { setFilter } from 'shared/store/src/lib/actions/filter';
import { setUserProfile } from 'shared/store/src/lib/actions/userprofile';
import { setBadge } from 'shared/store/src/lib/actions/badges';
import { setLevelImage } from 'shared/store/src/lib/actions/levelsImage';
import { setAvatar } from 'shared/store/src/lib/actions/avatars';
import { setProduct } from 'shared/store/src/lib/actions/products';
import { formatName } from 'shared/functions/home';

// Icons
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';

import './Navbar.scss';

type AdminNavbarProps = {
  routes?: {
    dashboard?: string;
    students?: string;
    reports?: string;
    settings?: string;
  };
};

// Match public Navbar scale
const NAV_FONT_SIZE = 18; // px
const ICON_SIZE = 22; // px
const BTN_PX = 3; // theme spacing (≈24px)
const BTN_PY = 0.75; // (≈6px)
const TOOLBAR_HEIGHT = 64; // px

const RouterNavLink = React.forwardRef<HTMLAnchorElement, NavLinkProps>(
  function RouterNavLink(props, ref) {
    return <NavLink ref={ref} {...props} />;
  }
);

export default function AdminNavbar({
  routes = {
    dashboard: '/school-admin-dashboard',
    students: '/school-users-management',
    reports: '/school-admin-reports',
    settings: '/school-admin-settings',
  },
}: AdminNavbarProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const db = getFirestore();

  const profile = useSelector((state: any) => state.userprofile);

  const [user, setUser] = useState<any>(null);
  const [adminName, setAdminName] = useState<string>('Admin');
  const [logoutAskOpen, setLogoutAskOpen] = useState(false);

  // Prefer name from custom_auth → user_profile → Auth fields → email local-part
  const resolveAdminDisplayName = async (currentUser: any) => {
    // 1) Try custom_auth by uid
    if (currentUser?.uid) {
      try {
        const caRef = doc(db, 'custom_auth', currentUser.uid);
        const caSnap = await getDoc(caRef);
        if (caSnap.exists()) {
          const data = caSnap.data() as any;
          const fromCustomAuth =
            data?.username || data?.full_name || data?.name || '';
          if (fromCustomAuth) return formatName(fromCustomAuth);
        }
      } catch (_) {}
    }

    // 2) Try custom_auth by email (fallback if docs keyed by email)
    if (currentUser?.email) {
      try {
        const q = query(
          collection(db, 'custom_auth'),
          where('email', '==', currentUser.email)
        );
        const snap = await getDocs(q);
        if (!snap.empty) {
          // take the first matching document's data
          const docData = snap.docs[0].data() as any;
          const fromCustomAuth =
            docData?.username || docData?.full_name || docData?.name || '';
          if (fromCustomAuth) return formatName(fromCustomAuth);
        }
      } catch (_) {}
    }

    // 3) Fallback to user_profile from Redux (if present already)
    const fromProfile = profile?.user?.full_name || profile?.user?.name || '';
    if (fromProfile) return formatName(fromProfile);

    // 4) Fallback to Auth displayName → email local-part → 'Admin'
    const fromAuthRaw =
      currentUser?.displayName ||
      (currentUser?.email ? currentUser.email.split('@')[0] : '') ||
      'Admin';
    const fromAuth =
      typeof fromAuthRaw === 'string' ? fromAuthRaw : String(fromAuthRaw);
    return formatName(fromAuth);
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(authen, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        const name = await resolveAdminDisplayName(currentUser);
        setAdminName(name || 'Admin');
      } else {
        setAdminName('Admin');
      }
    });
    return () => unsub();
    // Include profile dependency only for the fallback layer
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.user?.full_name]);

  const signOutUser = async () => {
    const auth = getAuth();
    const current = auth.currentUser;

    const questionInfo = {
      uid: '',
      mode: '',
      level: '',
      filter_type: '',
      filter_value: '',
      include_incorrect: '',
    };

    localStorage.removeItem('userData');
    localStorage.removeItem('pronunciationMode');
    localStorage.removeItem('userSpellingPreference');
    localStorage.removeItem('isLoggedInViaClever');
    localStorage.removeItem('GlobalSoundsControl');

    if (current) {
      await authen.signOut();
    }

    await dispatch(setFilter(questionInfo));
    await dispatch(setUserProfile(''));
    await dispatch(setBadge(null));
    await dispatch(setLevelImage([]));
    await dispatch(setAvatar([]));
    await dispatch(setProduct([]));

    setLogoutAskOpen(false);
    navigate('/login');
  };

  const linkSx = {
    my: 2,
    mx: 1,
    px: BTN_PX,
    py: BTN_PY,
    textTransform: 'none' as const,
    color: '#5E5E5E',
    fontWeight: 500,
    fontSize: `${NAV_FONT_SIZE}px`,
    '& .MuiButton-startIcon': {
      mr: 1,
      '& > svg': { fontSize: ICON_SIZE },
    },
    '&:hover': { color: '#2A9235' },
    '&[aria-current="page"]': { color: '#2A9235', fontWeight: 600 },
  };

  return (
    <>
      <AppBar
        position="fixed"
        color="default"
        sx={{ background: '#fff' }}
        className="navbar-new-section"
      >
        <Container sx={{ maxWidth: { xs: '100%', sm: '100%', md: '1460px' } }}>
          <Toolbar disableGutters sx={{ minHeight: TOOLBAR_HEIGHT }}>
            <Link to="/" className="logo-company">
              <img src={Logoapp} alt="Logo" style={{ marginRight: 16 }} />
            </Link>

            <Box
              sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}
            >
              <Button
                component={RouterNavLink}
                to={routes.dashboard!}
                sx={linkSx}
                startIcon={<DashboardOutlinedIcon />}
              >
                Dashboard
              </Button>
              <Button
                component={RouterNavLink}
                to={routes.students!}
                sx={linkSx}
                startIcon={<PeopleAltOutlinedIcon />}
              >
                Users
              </Button>
              {/* <Button
                component={RouterNavLink}
                to={routes.reports!}
                sx={linkSx}
                startIcon={<BarChartOutlinedIcon />}
              >
                Reports
              </Button>
              <Button
                component={RouterNavLink}
                to={routes.settings!}
                sx={linkSx}
                startIcon={<SettingsOutlinedIcon />}
              >
                Settings
              </Button> */}
              <Button
                onClick={() => setLogoutAskOpen(true)}
                sx={linkSx}
                startIcon={<LogoutOutlinedIcon />}
              >
                Logout
              </Button>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                variant="h6"
                noWrap
                sx={{
                  mr: 2,
                  color: '#2A9235',
                  fontSize: { xs: 12, sm: '1.25rem' },
                  fontWeight: 700,
                  userSelect: 'none',
                }}
              >
                {adminName}
              </Typography>
              <Avatar
                src={
                  user?.isAnonymous
                    ? usericon
                    : profile?.user?.img_uri_https || usericon
                }
                alt="Admin"
                sx={{ cursor: 'default' }}
              />
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <ConfirmModal
        open={logoutAskOpen}
        onClose={() => setLogoutAskOpen(false)}
        onConfirm={signOutUser}
      />

      <Box sx={{ height: TOOLBAR_HEIGHT }} />
    </>
  );
}
